/*
 * Shared Vehicles View Component - FIXED with Debug Logging
 * 
 * FIXES:
 * - Added comprehensive debug logging to identify data issues
 * - Implemented "recent GPS data" check (last 15 minutes)
 * - Separated GPS availability from sharing status
 * - Enhanced data validation and error handling
 */

import React, { useState, useEffect, useRef } from 'react';
import { getTimeSinceUpdate } from '../../utils/config';

/**
 * Check if GPS data is recent (within last 15 minutes)
 */
const isGPSDataRecent = (lastUpdated) => {
  if (!lastUpdated) return false;
  
  const now = new Date();
  const lastUpdate = new Date(lastUpdated);
  const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60 * 1000); // 15 minutes ago
  
  return lastUpdate >= fifteenMinutesAgo;
};

/**
 * Calculate sharing status based on schedule and current time
 */
const calculateSharingActive = (vehicle) => {
  // If temporarily disabled, sharing is not active
  if (vehicle.temporarily_disabled) {
    return false;
  }
  
  // If share at all times, sharing is always active
  if (vehicle.share_at_all_times) {
    return true;
  }
  
  // Check schedule for current day/time
  const now = new Date();
  const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  const currentTime = now.toTimeString().slice(0, 5); // HH:MM format
  
  const daySchedule = vehicle.schedule?.[currentDay];
  if (!daySchedule) {
    return false;
  }
  
  // Handle multiple time ranges or single time range
  if (Array.isArray(daySchedule)) {
    return daySchedule.some(timeRange => 
      currentTime >= timeRange.start_time && currentTime <= timeRange.end_time
    );
  } else if (daySchedule.start_time && daySchedule.end_time) {
    return currentTime >= daySchedule.start_time && currentTime <= daySchedule.end_time;
  }
  
  return false;
};

/**
 * Enhanced Schedule Display Component
 */
const ScheduleDisplay = ({ schedule, shareAtAllTimes, sharing_active }) => {
  const [showFullSchedule, setShowFullSchedule] = useState(false);

  if (shareAtAllTimes) {
    return (
      <div className="space-y-1">
        <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
          🕐 Available All Times
        </span>
        <p className="text-xs text-blue-600">24/7 access enabled</p>
      </div>
    );
  }

  const activeDays = Object.keys(schedule || {}).filter(day => schedule[day]);
  
  if (activeDays.length === 0) {
    return (
      <div className="space-y-1">
        <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
          📅 No Schedule Set
        </span>
        <p className="text-xs text-gray-500">Contact owner for availability</p>
      </div>
    );
  }

  const dayAbbrevs = {
    monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
    friday: 'Fri', saturday: 'Sat', sunday: 'Sun'
  };

  const dayNames = {
    monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday',
    friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday'
  };

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  const todaySchedule = schedule[today];

  return (
    <div className="space-y-2">
      {todaySchedule && (
        <div className={`p-2 rounded-md ${sharing_active ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`}>
          <div className={`flex items-center justify-between text-xs ${sharing_active ? 'text-green-700' : 'text-blue-700'}`}>
            <span className="font-medium">Today ({dayNames[today]}):</span>
            {sharing_active && <span className="text-green-600">🟢 ACTIVE</span>}
          </div>
          <div className="mt-1">
            {Array.isArray(todaySchedule) ? (
              todaySchedule.map((timeRange, index) => (
                <div key={index} className={`text-xs font-medium ${sharing_active ? 'text-green-800' : 'text-blue-800'}`}>
                  📅 {timeRange.start_time} - {timeRange.end_time}
                </div>
              ))
            ) : (
              <div className={`text-xs font-medium ${sharing_active ? 'text-green-800' : 'text-blue-800'}`}>
                📅 {todaySchedule.start_time} - {todaySchedule.end_time}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          <span>{activeDays.length} {activeDays.length === 1 ? 'day' : 'days'} scheduled</span>
        </div>
        <button
          onClick={() => setShowFullSchedule(!showFullSchedule)}
          className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
        >
          {showFullSchedule ? 'Hide' : 'Show'} Full Schedule
          <span className="ml-1">{showFullSchedule ? '▲' : '▼'}</span>
        </button>
      </div>

      {showFullSchedule && (
        <div className="border rounded-md p-2 bg-gray-50">
          <div className="text-xs font-medium text-gray-700 mb-2">Weekly Schedule:</div>
          <div className="space-y-1 text-xs">
            {activeDays.map(day => (
              <div key={day} className="flex justify-between">
                <span className="capitalize">{day}:</span>
                <span className="text-gray-600">
                  {Array.isArray(schedule[day]) 
                    ? schedule[day].map(tr => `${tr.start_time}-${tr.end_time}`).join(', ')
                    : `${schedule[day].start_time}-${schedule[day].end_time}`
                  }
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-center">
        {sharing_active ? (
          <span className="text-green-600 text-xs font-medium">🟢 Active Now</span>
        ) : (
          <span className="text-gray-400 text-xs">⏰ Scheduled</span>
        )}
      </div>
    </div>
  );
};

/**
 * Debug Panel Component - Shows vehicle data for troubleshooting
 */
const DebugPanel = ({ vehicle, isVisible, onToggle }) => {
  if (!isVisible) {
    return (
      <button
        onClick={onToggle}
        className="mt-2 text-xs text-gray-400 hover:text-gray-600"
      >
        🔍 Debug Info
      </button>
    );
  }

  return (
    <div className="mt-3 p-2 bg-gray-100 border rounded text-xs">
      <div className="flex justify-between items-center mb-2">
        <span className="font-bold text-gray-700">Debug Data:</span>
        <button onClick={onToggle} className="text-gray-500 hover:text-gray-700">✕</button>
      </div>
      <div className="space-y-1 text-gray-600 font-mono">
        <div><strong>sharing_active:</strong> {String(vehicle.sharing_active)} (from backend)</div>
        <div><strong>calculated_active:</strong> {String(calculateSharingActive(vehicle))}</div>
        <div><strong>temporarily_disabled:</strong> {String(vehicle.temporarily_disabled)}</div>
        <div><strong>share_at_all_times:</strong> {String(vehicle.share_at_all_times)}</div>
        <div><strong>last_location exists:</strong> {String(!!vehicle.last_location)}</div>
        <div><strong>last_updated:</strong> {vehicle.last_updated || 'null'}</div>
        <div><strong>gps_recent:</strong> {String(isGPSDataRecent(vehicle.last_updated))}</div>
        <div><strong>vehicle_details:</strong> {vehicle.vehicle_details ? 'exists' : 'missing'}</div>
        <div><strong>make:</strong> {vehicle.make || vehicle.vehicle_details?.make || 'missing'}</div>
        <div><strong>model:</strong> {vehicle.model || vehicle.vehicle_details?.model || 'missing'}</div>
        {vehicle.last_location && (
          <>
            <div><strong>lat:</strong> {vehicle.last_location.latitude}</div>
            <div><strong>lng:</strong> {vehicle.last_location.longitude}</div>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Shared Vehicles View Component - ENHANCED with Debug Logging
 */
const SharedVehiclesView = ({ vehicles, onRefresh, onVehicleClick }) => {
  const [debugVisible, setDebugVisible] = useState({});

  // Add debug logging for incoming data (with dependency check to prevent double logging)
  const vehiclesRef = useRef(JSON.stringify(vehicles));
  
  useEffect(() => {
    const currentVehiclesStr = JSON.stringify(vehicles);
    if (vehiclesRef.current !== currentVehiclesStr) {
      vehiclesRef.current = currentVehiclesStr;
      
      if (vehicles.length === 0) {
      } else {
        vehicles.forEach((vehicle, index) => {
        });
        
        // Backend API Check
        const firstVehicle = vehicles[0];
        const missingFields = [];
        if (!firstVehicle.make && !firstVehicle.vehicle_details?.make) missingFields.push('make');
        if (!firstVehicle.model && !firstVehicle.vehicle_details?.model) missingFields.push('model');
        if (!firstVehicle.license_plate && !firstVehicle.vehicle_details?.license_plate) missingFields.push('license_plate');
        if (!firstVehicle.last_location) missingFields.push('last_location');
        if (!firstVehicle.last_updated) missingFields.push('last_updated');
        
        if (missingFields.length > 0) {
        }
      }
    }
  }, [vehicles]);

  const toggleDebug = (vehicleId) => {
    setDebugVisible(prev => ({
      ...prev,
      [vehicleId]: !prev[vehicleId]
    }));
  };

  const handleVehicleClick = (vehicle) => {
    
    // Calculate sharing status if not provided by backend
    const calculatedSharingActive = vehicle.sharing_active !== undefined 
      ? vehicle.sharing_active 
      : calculateSharingActive(vehicle);
    
    // Check if GPS data is recent (last 15 minutes)
    const hasRecentGPS = vehicle.last_location && isGPSDataRecent(vehicle.last_updated);
    
    if (calculatedSharingActive && hasRecentGPS && onVehicleClick) {
      onVehicleClick(vehicle);
    } else {
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Shared Vehicles</h2>
          <p className="text-sm text-gray-600 mt-1">
            Vehicles shared with you by other users
          </p>
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <span className="mr-2">🔄</span>
          Refresh
        </button>
      </div>

      {vehicles.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🤝</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Shared Vehicles</h3>
          <p className="text-gray-600 mb-6">
            No vehicles have been shared with you yet. Ask colleagues or friends to share their vehicles for carpooling.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vehicles.map((vehicle) => {
            // Calculate sharing status (use backend value if available, otherwise calculate)
            const calculatedSharingActive = vehicle.sharing_active !== undefined 
              ? vehicle.sharing_active 
              : calculateSharingActive(vehicle);
            
            // Check if GPS data is recent
            const hasRecentGPS = vehicle.last_location && isGPSDataRecent(vehicle.last_updated);
            
            return (
              <div
                key={vehicle.id}
                onClick={() => handleVehicleClick(vehicle)}
                className={`border-2 rounded-lg p-4 transition-all duration-200 ${
                  calculatedSharingActive && hasRecentGPS
                    ? 'border-green-200 bg-green-50 hover:shadow-lg hover:scale-105 cursor-pointer'
                    : vehicle.temporarily_disabled
                    ? 'border-red-200 bg-red-50 cursor-not-allowed'
                    : 'border-gray-200 bg-gray-50 cursor-not-allowed'
                }`}
              >
                {/* Vehicle Header */}
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-900">
                      {(vehicle.year || vehicle.vehicle_details?.year || '????')} {' '}
                      {(vehicle.make || vehicle.vehicle_details?.make || 'Unknown')} {' '}
                      {(vehicle.model || vehicle.vehicle_details?.model || 'Vehicle')}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Shared by: {vehicle.owner_email || 'Unknown'}
                    </p>
                    {(!vehicle.make && !vehicle.vehicle_details?.make) && (
                      <p className="text-xs text-red-600 mt-1">
                        ⚠️ Vehicle details missing from backend
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end space-y-1">
                    {/* Sharing Status */}
                    {vehicle.temporarily_disabled ? (
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full font-medium">
                        🚫 Disabled
                      </span>
                    ) : calculatedSharingActive ? (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                        ✅ Active Now
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        ⏰ Scheduled
                      </span>
                    )}
                    
                    {/* GPS Status - FIXED: Now checks for recent GPS data */}
                    {hasRecentGPS ? (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                        📍 Live Tracking
                      </span>
                    ) : vehicle.last_location ? (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full font-medium">
                        📍 GPS Stale
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        📍 No GPS Data
                      </span>
                    )}
                    
                    {/* Vehicle Type */}
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                      🤝 Shared
                    </span>
                  </div>
                </div>

                {/* Vehicle Details */}
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="font-medium text-gray-800">License:</p>
                      <p>{vehicle.license_plate || vehicle.vehicle_details?.license_plate || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Color:</p>
                      <p>{vehicle.color || vehicle.vehicle_details?.color || 'N/A'}</p>
                    </div>
                  </div>
                  
                  <div className="pt-2 border-t border-gray-200">
                    <p className="font-medium text-gray-800 mb-2">Sharing Schedule:</p>
                    <ScheduleDisplay 
                      schedule={vehicle.schedule || {}} 
                      shareAtAllTimes={vehicle.share_at_all_times}
                      sharing_active={calculatedSharingActive}
                    />
                  </div>

                  {/* Last Location Info - ENHANCED with recent GPS check */}
                  {vehicle.last_location && (
                    <div className={`pt-2 border-t ${hasRecentGPS ? 'border-green-200' : 'border-yellow-200'}`}>
                      <p className="font-medium text-gray-800">Last Update:</p>
                      <p className="text-xs">{getTimeSinceUpdate(vehicle.last_updated)}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {vehicle.last_location.latitude?.toFixed(4)}, {vehicle.last_location.longitude?.toFixed(4)}
                      </p>
                      {!hasRecentGPS && (
                        <p className="text-xs text-yellow-600 mt-1">
                          ⚠️ GPS data is older than 15 minutes
                        </p>
                      )}
                    </div>
                  )}

                  {/* Shared Date */}
                  <div className="pt-2 border-t border-gray-200">
                    <p className="font-medium text-gray-800">Shared:</p>
                    <p className="text-xs">{new Date(vehicle.shared_at || vehicle.created_at).toLocaleDateString()}</p>
                  </div>
                </div>

                {/* Click Action Indicator - ENHANCED */}
                <div className="mt-4 pt-3 border-t border-gray-200">
                  {calculatedSharingActive && hasRecentGPS ? (
                    <p className="text-xs text-green-600 font-medium flex items-center">
                      <span className="mr-2">🗺️</span>
                      Click to view on map
                    </p>
                  ) : vehicle.temporarily_disabled ? (
                    <p className="text-xs text-red-600 flex items-center">
                      <span className="mr-2">🚫</span>
                      Sharing temporarily disabled
                    </p>
                  ) : !calculatedSharingActive ? (
                    <p className="text-xs text-gray-500 flex items-center">
                      <span className="mr-2">⏰</span>
                      Available during scheduled times
                    </p>
                  ) : (
                    <p className="text-xs text-yellow-600 flex items-center">
                      <span className="mr-2">📍</span>
                      GPS data too old for live tracking
                    </p>
                  )}
                </div>

                {/* Debug Panel */}
                <DebugPanel
                  vehicle={vehicle}
                  isVisible={debugVisible[vehicle.id]}
                  onToggle={() => toggleDebug(vehicle.id)}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Enhanced Statistics */}
      {vehicles.length > 0 && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-green-600 text-2xl mr-3">✅</div>
              <div>
                <p className="text-sm text-green-600 font-medium">Active Now</p>
                <p className="text-2xl font-bold text-green-800">
                  {vehicles.filter(v => (v.sharing_active !== undefined ? v.sharing_active : calculateSharingActive(v))).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-blue-600 text-2xl mr-3">📍</div>
              <div>
                <p className="text-sm text-blue-600 font-medium">Live GPS</p>
                <p className="text-2xl font-bold text-blue-800">
                  {vehicles.filter(v => v.last_location && isGPSDataRecent(v.last_updated)).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-orange-600 text-2xl mr-3">🕐</div>
              <div>
                <p className="text-sm text-orange-600 font-medium">All Times</p>
                <p className="text-2xl font-bold text-orange-800">
                  {vehicles.filter(v => v.share_at_all_times).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-purple-600 text-2xl mr-3">🤝</div>
              <div>
                <p className="text-sm text-purple-600 font-medium">Total Shared</p>
                <p className="text-2xl font-bold text-purple-800">
                  {vehicles.length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Instructions */}
      <div className="mt-6 text-center text-sm text-gray-500 bg-gray-50 rounded-lg p-4">
        <p className="font-medium mb-2">📱 Shared Vehicle Access:</p>
        <div className="space-y-1 text-xs">
          <p>• <strong>Green vehicles</strong> are currently accessible with live GPS (last 15 min)</p>
          <p>• <strong>Gray vehicles</strong> are only available during scheduled times</p>
          <p>• <strong>Yellow GPS</strong> indicates stale location data (older than 15 min)</p>
          <p>• <strong>Red vehicles</strong> have been temporarily disabled by the owner</p>
          <p>• <strong>Click 🔍 Debug Info</strong> to see detailed vehicle data</p>
        </div>
      </div>
    </div>
  );
};

export default SharedVehiclesView;